import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  styled,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  Link,
} from '@mui/material';
import {
  Question,
  NestedQuestion,
  UserResponse,
} from '../../../../components/Global/DansFundQuestionnaire/Common';

// Styled components optimized for mobile with enhanced visual hierarchy
const QuestionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontWeight: 600,
  fontSize: '1.1rem',
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  position: 'relative',
  paddingBottom: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    opacity: 0.7,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(1, 0),
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiFormControlLabel-root': {
    marginBottom: theme.spacing(1.5),
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.15s ease-in-out',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '& .MuiFormControlLabel-label': {
    fontWeight: 500,
    fontSize: '1rem',
  },
  '& .MuiRadio-root': {
    padding: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  '& .Mui-checked': {
    '& + .MuiFormControlLabel-label': {
      fontWeight: 600,
    },
  },
}));

const FollowUpContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  paddingLeft: theme.spacing(3),
  borderLeft: `2px solid ${theme.palette.grey[300]}`,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  position: 'relative',
  backgroundColor: 'rgba(0, 0, 0, 0.01)',
  borderRadius: '0 8px 8px 0',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: -8,
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[300],
  },
}));

const YesFollowUpContainer = styled(FollowUpContainer)(({ theme }) => ({
  borderLeft: '2px solid #4caf50',
  backgroundColor: 'rgba(76, 175, 80, 0.03)',
  '&:before': {
    backgroundColor: '#4caf50',
  },
  '&:after': {
    content: '"Yes"',
    position: 'absolute',
    top: -10,
    left: -30,
    fontSize: '0.7rem',
    color: '#4caf50',
    fontWeight: 600,
    backgroundColor: 'white',
    padding: '0 4px',
    borderRadius: '2px',
  },
}));

const NoFollowUpContainer = styled(FollowUpContainer)(({ theme }) => ({
  borderLeft: '2px solid #f44336',
  backgroundColor: 'rgba(244, 67, 54, 0.03)',
  '&:before': {
    backgroundColor: '#f44336',
  },
  '&:after': {
    content: '"No"',
    position: 'absolute',
    top: -10,
    left: -28,
    fontSize: '0.7rem',
    color: '#f44336',
    fontWeight: 600,
    backgroundColor: 'white',
    padding: '0 4px',
    borderRadius: '2px',
  },
}));

const InfoMessage = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  backgroundColor: 'rgba(33, 150, 243, 0.08)',
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  position: 'relative',
  borderLeft: '2px solid rgba(33, 150, 243, 0.5)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  '&:before': {
    content: '"ℹ️"',
    position: 'absolute',
    left: -12,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '0.85rem',
  },
}));

interface QuestionRendererProps {
  question: Question;
  onOptionSelected: (response: UserResponse) => void;
}

// Utility function to make links and phone numbers clickable
const makeTextClickable = (text: string): React.ReactNode => {
  if (!text) return '';

  // Regular expressions for emails, URLs, and phone numbers
  // Email regex comes first to ensure it takes priority
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  // Updated URL regex to handle various domain patterns while excluding email addresses
  const urlRegex = /(https?:\/\/[^\s<>"]+)|(?:^|\s)((?:www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*(?:\.[a-zA-Z]{2,})+(?:\/[^\s<>"]*)?)(?=\b|$|[.,!?;:])/g;
  const phoneRegex = /(\+?[\d\s()-]{10,})/g;

  // Process the text in stages to handle each type of link
  const processText = (input: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    let lastIndex = 0;

    // First process URLs
    const withUrls: Array<string | React.ReactNode> = [];
    let match;
    
    // Reset the regex lastIndex to ensure we start from the beginning
    urlRegex.lastIndex = 0;
    
    while ((match = urlRegex.exec(input)) !== null) {
      // If there's text before this match, add it
      if (match.index > lastIndex) {
        withUrls.push(input.substring(lastIndex, match.index));
      }
      
      // Get the URL part (group 2 for domain-only URLs, group 1 for full URLs)
      let urlMatch = match[1] || match[2];
      if (!urlMatch) continue;
      
      // Clean up the URL and prepare the href
      const url = urlMatch.trim();
      const hrefUrl = url.startsWith('http') ? url : 
                    url.startsWith('www.') ? `https://${url}` : 
                    `https://www.${url}`;
      
      // Update lastIndex to the end of the match
      lastIndex = match.index + match[0].length;
      
      withUrls.push(
        <Link
          key={`url-${match.index}`}
          href={hrefUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: 'break-word' }}
        >
          {url}
        </Link>
      );
    }
    
    // Add any remaining text after processing URLs
    if (lastIndex < input.length) {
      withUrls.push(input.substring(lastIndex));
    }
    
    // Reset lastIndex for the next processing stage
    lastIndex = 0;

    // Then process emails in each text segment
    const withEmails = withUrls.flatMap(segment => {
      if (typeof segment !== 'string') return segment;
      
      const segmentParts: React.ReactNode[] = [];
      let emailLastIndex = 0;
      let emailMatch;
      
      while ((emailMatch = emailRegex.exec(segment)) !== null) {
        if (emailMatch.index > emailLastIndex) {
          segmentParts.push(segment.substring(emailLastIndex, emailMatch.index));
        }
        
        const email = emailMatch[0];
        segmentParts.push(
          <Link
            key={`email-${emailMatch.index}`}
            href={`mailto:${email}`}
            sx={{ wordBreak: 'break-word' }}
          >
            {email}
          </Link>
        );
        
        emailLastIndex = emailMatch.index + email.length;
      }
      
      if (emailLastIndex < segment.length) {
        segmentParts.push(segment.substring(emailLastIndex));
      }
      
      return segmentParts;
    });

    // Finally process phone numbers in each text segment
    const withPhones = withEmails.flatMap(segment => {
      if (typeof segment !== 'string') return segment;
      
      const segmentParts: React.ReactNode[] = [];
      let phoneLastIndex = 0;
      let phoneMatch;
      
      while ((phoneMatch = phoneRegex.exec(segment)) !== null) {
        if (phoneMatch.index > phoneLastIndex) {
          segmentParts.push(segment.substring(phoneLastIndex, phoneMatch.index));
        }
        
        const phone = phoneMatch[0];
        const formattedPhone = phone.replace(/\s+/g, '');
        
        segmentParts.push(
          <Link
            key={`phone-${phoneMatch.index}`}
            href={`tel:${formattedPhone}`}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {phone}
          </Link>
        );
        
        phoneLastIndex = phoneMatch.index + phone.length;
      }
      
      if (phoneLastIndex < segment.length) {
        segmentParts.push(segment.substring(phoneLastIndex));
      }
      
      return segmentParts;
    });

    return withPhones;
  };

  const processedText = processText(text);
  return processedText.length === 1 && typeof processedText[0] === 'string' 
    ? processedText[0] 
    : <>{processedText}</>;
};

// Component to render a nested question
const NestedQuestionRenderer: React.FC<{
  question: NestedQuestion;
  parentResponse: UserResponse;
  onNestedResponse: (parentResponse: UserResponse, nestedResponse: UserResponse) => void;
}> = ({ question, parentResponse, onNestedResponse }) => {
  const [selectedOption, setSelectedOption] = useState<'yes' | 'no' | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  // Handle option change and submit response only once
  const handleOptionChange = useCallback((option: 'yes' | 'no') => {
    setSelectedOption(option);

    if (!hasSubmitted) {
      // Create the nested response
      const nestedResponse: UserResponse = {
        questionId: question.id,
        answer: option,
        responseMessage: option === 'yes' ? question.yesInfoMessage : question.noInfoMessage,
      };

      // Pass it up to the parent
      onNestedResponse(parentResponse, nestedResponse);
      setHasSubmitted(true);
    }
  }, [question.id, question.yesInfoMessage, question.noInfoMessage, parentResponse, onNestedResponse, hasSubmitted]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'yes' | 'no';
    handleOptionChange(value);
  };

  return (
    <Box sx={{ mt: 1.5, mb: 1.5 }}>
      <Typography variant="body1" fontWeight="medium"
                  sx={{ fontSize: '0.95rem', mb: 1 }}>
        {makeTextClickable(question.text)}
      </Typography>

      <StyledFormControl>
        <StyledRadioGroup
          aria-label="nested-question-options"
          name="nested-question-options"
          value={selectedOption || ''}
          onChange={handleRadioChange}
        >
          <Box>
            <StyledFormControlLabel
              value="yes"
              control={<Radio size="small" />}
              label="Yes"
            />
            {selectedOption === 'yes' && question.yesInfoMessage && (
              <InfoMessage>
                {makeTextClickable(question.yesInfoMessage)}
              </InfoMessage>
            )}
            {selectedOption === 'yes' && question.yesFollowUps && question.yesFollowUps.length > 0 && (
              <YesFollowUpContainer>
                {question.yesFollowUps.map((followUp, index) => (
                  <NestedQuestionRenderer
                    key={followUp.id}
                    question={followUp}
                    parentResponse={{
                      ...parentResponse,
                      questionId: question.id,
                      answer: 'yes',
                      responseMessage: question.yesInfoMessage,
                    }}
                    onNestedResponse={onNestedResponse}
                  />
                ))}
              </YesFollowUpContainer>
            )}
          </Box>

          <Box>
            <StyledFormControlLabel
              value="no"
              control={<Radio size="small" />}
              label="No"
            />
            {selectedOption === 'no' && question.noInfoMessage && (
              <InfoMessage>
                {makeTextClickable(question.noInfoMessage)}
              </InfoMessage>
            )}
            {selectedOption === 'no' && question.noFollowUps && question.noFollowUps.length > 0 && (
              <NoFollowUpContainer>
                {question.noFollowUps.map((followUp, index) => (
                  <NestedQuestionRenderer
                    key={followUp.id}
                    question={followUp}
                    parentResponse={{
                      ...parentResponse,
                      questionId: question.id,
                      answer: 'no',
                      responseMessage: question.noInfoMessage,
                    }}
                    onNestedResponse={onNestedResponse}
                  />
                ))}
              </NoFollowUpContainer>
            )}
          </Box>
        </StyledRadioGroup>
      </StyledFormControl>
    </Box>
  );
};

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
                                                             question,
                                                             onOptionSelected,
                                                           }) => {
  const [selectedOption, setSelectedOption] = useState<'yes' | 'no' | null>(null);
  // eslint-disable-next-line
  const [_, setNestedResponses] = useState<UserResponse[]>([]);

  // Handle option selection with useCallback to prevent unnecessary re-renders
  const handleOptionSelection = useCallback((option: 'yes' | 'no') => {
    // Create the response
    const response: UserResponse = {
      questionId: question.id,
      answer: option,
      responseMessage: option === 'yes' ? question.yesInfoMessage : question.noInfoMessage,
    };

    // Pass it to the parent component
    onOptionSelected(response);
  }, [question.id, question.yesInfoMessage, question.noInfoMessage, onOptionSelected]);

  // Auto-save answer when option is selected
  useEffect(() => {
    if (selectedOption) {
      handleOptionSelection(selectedOption);
    }
    // Only depend on selectedOption and the stable handler
  }, [selectedOption, handleOptionSelection]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as 'yes' | 'no');
    // Clear nested responses when the main answer changes
    setNestedResponses([]);
  };

  // Memoize the nested response handler to prevent unnecessary re-renders
  const handleNestedResponse = useCallback((parentResponse: UserResponse, nestedResponse: UserResponse) => {
    // Add the nested response
    setNestedResponses(prevResponses => {
      // Check if this response already exists to prevent duplicates
      const existingIndex = prevResponses.findIndex(
        resp => resp.questionId === nestedResponse.questionId,
      );

      let updatedNestedResponses;
      if (existingIndex >= 0) {
        // Update existing response instead of adding a new one
        updatedNestedResponses = [...prevResponses];
        updatedNestedResponses[existingIndex] = nestedResponse;
      } else {
        // Add new response
        updatedNestedResponses = [...prevResponses, nestedResponse];
      }

      // Create the complete response with all nested responses
      const completeResponse: UserResponse = {
        ...parentResponse,
        nestedResponses: updatedNestedResponses,
      };

      // Use requestAnimationFrame to defer the state update to the next frame
      // This prevents the "Maximum update depth exceeded" error
      requestAnimationFrame(() => {
        // Pass it up to the parent component
        onOptionSelected(completeResponse);
      });

      return updatedNestedResponses;
    });
  }, [onOptionSelected]);


  return (
    <QuestionPaper>
      <QuestionText variant="body1">
        {makeTextClickable(question.text)}
      </QuestionText>

      <StyledFormControl>
        <StyledRadioGroup
          aria-label="question-options"
          name="question-options"
          value={selectedOption || ''}
          onChange={handleRadioChange}
        >
          <Box>
            <StyledFormControlLabel
              value="yes"
              control={<Radio />}
              label="Yes"
            />
            {selectedOption === 'yes' && question.yesInfoMessage && (
              <InfoMessage>
                {makeTextClickable(question.yesInfoMessage)}
              </InfoMessage>
            )}
            {selectedOption === 'yes' && question.yesFollowUps && question.yesFollowUps.length > 0 && (
              <YesFollowUpContainer>
                {question.yesFollowUps.map((followUp, index) => (
                  <NestedQuestionRenderer
                    key={followUp.id}
                    question={followUp}
                    parentResponse={{
                      questionId: question.id,
                      answer: 'yes',
                      responseMessage: question.yesInfoMessage,
                    }}
                    onNestedResponse={handleNestedResponse}
                  />
                ))}
              </YesFollowUpContainer>
            )}
          </Box>

          <Box>
            <StyledFormControlLabel
              value="no"
              control={<Radio />}
              label="No"
            />
            {selectedOption === 'no' && question.noInfoMessage && (
              <InfoMessage>
                {makeTextClickable(question.noInfoMessage)}
              </InfoMessage>
            )}
            {selectedOption === 'no' && question.noFollowUps && question.noFollowUps.length > 0 && (
              <NoFollowUpContainer>
                {question.noFollowUps.map((followUp, index) => (
                  <NestedQuestionRenderer
                    key={followUp.id}
                    question={followUp}
                    parentResponse={{
                      questionId: question.id,
                      answer: 'no',
                      responseMessage: question.noInfoMessage,
                    }}
                    onNestedResponse={handleNestedResponse}
                  />
                ))}
              </NoFollowUpContainer>
            )}
          </Box>
        </StyledRadioGroup>
      </StyledFormControl>
    </QuestionPaper>
  );
};

export default QuestionRenderer;
